import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { timeout } from 'rxjs/operators';
import { IAuthStrategy } from '../interfaces/auth-strategy.interface';
import { API_CLIENT_CONFIG } from '../tokens/api-config.token';

/**
 * Azure Easy Auth から返却される標準的なトークンレスポンス構造のインターフェース
 */
interface EasyAuthTokenPayload {
  access_token: string;
  user_id?: string;
  expires_on?: string;
}

/**
 * 社内（内網）環境専用の認証サービス
 * @description
 * 認証基盤として「Azure Entra ID（シームレスSSO）」を利用します。
 */
@Injectable()
export class InternalAuthService implements IAuthStrategy {
  private readonly http = inject(HttpClient);
  private readonly config = inject(API_CLIENT_CONFIG);

  /**
   * トークン取得フロー（/.auth/me の呼出）
   */
  async acquireToken(): Promise<void> {
    if (!this.config.authApiEnabled) {
      console.log('社内認証APIは無効化されています。ダミートークンを使用します。');
      sessionStorage.setItem('access_token', this.config.dummyToken || '');
      sessionStorage.setItem('user_id', 'dummyUser');
      const expiresOnMs = Date.now() + 3600 * 1000; // 1時間後を仮の有効期限とする
      sessionStorage.setItem('expires_on', JSON.stringify(expiresOnMs));
      return;
    }
    const targetAuthUrl = (this.config as any).internalAuthUrl || this.config.authUrl;

    try {
      const timeoutMs = this.config.apiRequestUrlTimeout ?? 120000;

      // Azure Easy Auth は通常、TokenPayload を含む配列または直接のオブジェクトを返却します
      // 画面側の利用例に準拠し、ジェネリクスを用いて安全に型を決定します
      const response = await firstValueFrom(this.http.get<EasyAuthTokenPayload[] | EasyAuthTokenPayload>(targetAuthUrl, { withCredentials: true }).pipe(timeout(timeoutMs)));

      // 配列形式とオブジェクト形式のどちらの Easy Auth レスポンスでも安全に解決できるようオプショナルチェーンを適用
      const tokenData = Array.isArray(response) ? response[0] : response;

      if (tokenData?.access_token === undefined) {
        // 利用ガイドの条件分岐「error.status === 401」で確実にキャッチさせるため、
        // 独自の例外ではなく、Angular の標準的な HttpErrorResponse を構築してスローします
        throw new HttpErrorResponse({
          status: 401,
          statusText: 'Unauthorized',
          url: this.config.authUrl,
          error: { error: 'access_tokenがレスポンスに含まれていません。' },
        });
      }

      // 取得したトークン情報をセッションストレージに保存
      sessionStorage.setItem('access_token', tokenData.access_token);
      if (tokenData.user_id) {
        sessionStorage.setItem('user_id', tokenData.user_id);
      }
      if (tokenData.expires_on) {
        // 比較用に絶対ミリ秒に変換して格納
        const expiresOnMs = new Date(tokenData.expires_on).getTime();
        sessionStorage.setItem('expires_on', JSON.stringify(expiresOnMs));
      }
    } catch (error: any) {
      console.log(error);

      // すでに HttpErrorResponse であればそのまま上位へスロー
      if (error instanceof HttpErrorResponse) {
        throw error;
      }

      // その他の通信エラー（タイムアウト等）の場合もHttpErrorResponse にラップして投げる
      throw new HttpErrorResponse({
        status: error.status || 500,
        statusText: error.message || 'Internal Server Error',
        url: this.config.authUrl,
        error: error,
      });
    }
  }

  /**
   * トークン再発行（/.auth/refresh の呼出）
   */
  async handleTokenRefreshOrRedirect(): Promise<void> {
    try {
      const timeoutMs = this.config.apiRequestUrlTimeout ?? 120000;
      // refresh URLを実行
      await firstValueFrom(this.http.get<any>(this.config.authRefreshUrl, { withCredentials: true }).pipe(timeout(timeoutMs)));
      // 再発行成功後、トークン情報を最新化するためにacquireTokenを再帰実行
      await this.acquireToken();
    } catch (error: any) {
      console.error('社内トークンリフレッシュエラー', error);

      // リフレッシュ自体の失敗も、利用ガイド側でシステムエラーとして統一ハンドルさせるため HttpErrorResponse で投げます
      if (error instanceof HttpErrorResponse) {
        throw error;
      }
      throw new HttpErrorResponse({
        status: 500,
        statusText: 'Token Refresh Failed',
        url: this.config.authRefreshUrl,
      });
    }
  }
}
